/**
 * Authorize input + result banner.
 *
 * NIT-002: the banner auto-dismisses 6 s after it appears (matching
 * evse2's _authBannerTimer). Replacing the banner with a fresh result
 * resets the timer.
 *
 * NIT-005: idTag input gets a placeholder.
 *
 * NIT-007: Authorize button is disabled when the station is not
 * Connected. Hover tooltip explains.
 */
import { useEffect } from 'react';
import { ConnectionState } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useUIStore } from '@/state/useUIStore';

const STATUS_COLOR: Record<string, string> = {
  Accepted: 'bg-primary-hover text-primary-on',
  Blocked: 'bg-rose-700 text-rose-50',
  Expired: 'bg-amber-700 text-amber-50',
  Invalid: 'bg-rose-700 text-rose-50',
  ConcurrentTx: 'bg-amber-700 text-amber-50',
  Pending: 'bg-slate-700 text-slate-100',
};

const BANNER_AUTO_DISMISS_MS = 6_000;

export function AuthBanner(): JSX.Element {
  const banner = useStationStore((s) => s.authBanner);
  const authorize = useStationStore((s) => s.authorize);
  const connectionState = useStationStore((s) => s.connectionState);
  const input = useUIStore((s) => s.authorizeInput);
  const setInput = useUIStore((s) => s.setAuthorizeInput);
  const cls = banner ? (STATUS_COLOR[banner.status] ?? STATUS_COLOR.Pending) : '';
  const isConnected = connectionState === ConnectionState.Connected;

  // NIT-002 — auto-dismiss after 6s. `banner?.at` changes whenever a new
  // banner replaces the current one, so the timer resets correctly.
  useEffect(() => {
    if (!banner) return;
    const timer = setTimeout(() => {
      // Only clear if THIS banner is still the active one.
      const cur = useStationStore.getState().authBanner;
      if (cur && cur.at === banner.at) {
        useStationStore.setState({ authBanner: null });
      }
    }, BANNER_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [banner]);

  return (
    <section
      aria-label="Authorize"
      className="relative flex flex-wrap items-center gap-3 overflow-hidden rounded border border-slate-700 bg-slate-900/60 px-4 py-3"
    >
      <label className="text-sm text-slate-300" htmlFor="auth-idtag">
        idTag
      </label>
      <input
        id="auth-idtag"
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="DEMO_TAG"
        className="rounded border border-slate-600 bg-slate-950 px-2 py-1 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-base"
        data-testid="idtag-input"
      />
      <button
        type="button"
        disabled={!isConnected}
        title={isConnected ? undefined : 'Connect first'}
        onClick={() => {
          void authorize(input);
        }}
        className="rounded bg-slate-700 px-3 py-1 text-sm text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
        data-testid="authorize"
      >
        Authorize
      </button>
      {banner ? (
        <span
          className={`rounded px-2 py-1 text-xs font-medium ${cls}`}
          data-testid="auth-status"
          aria-live="polite"
        >
          {banner.idTag} → {banner.status}
        </span>
      ) : null}
      {/* BUG-008 — visual countdown bar at the bottom edge of the banner.
          Pure CSS animation (no JS timer interaction) tied to the same
          BANNER_AUTO_DISMISS_MS = 6 s budget. Keyed by banner.at so a
          new banner restarts the animation cleanly. */}
      {banner ? (
        <div
          key={banner.at}
          aria-hidden="true"
          data-testid="auth-countdown"
          className="evse3-banner-decay absolute bottom-0 left-0 h-1 bg-primary-base/60"
        />
      ) : null}
    </section>
  );
}
